import { useRef, useState } from "react";
import { Paperclip } from "lucide-react";
import { http } from "../api/http";
import type { MessageFile } from "../api/messaging.types";
export function MessageFiles({
  conversation,
  files,
  onError,
}: {
  conversation: string;
  files: MessageFile[];
  onError: (error: string) => void;
}) {
  async function download(file: MessageFile) {
    try {
      const { data } = await http.get(
        `/v1/messaging/conversations/${conversation}/files/${file.id}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      onError("Fichier indisponible ou accès retiré.");
    }
  }
  if (!files.length) return null;
  return (
    <ul className="messaging-files">
      {files.map((f) => (
        <li key={f.id}>
          <button type="button" onClick={() => void download(f)}>
            📎 {f.name} ({Math.ceil(f.size / 1024)} Kio)
          </button>
        </li>
      ))}
    </ul>
  );
}
export function MessageUpload({
  conversation,
  files,
  onFiles,
  onBusy,
  onError,
  disabled,
}: {
  conversation: string;
  files: MessageFile[];
  onFiles: (files: MessageFile[]) => void;
  onBusy: (busy: boolean) => void;
  onError: (error: string) => void;
  disabled: boolean;
}) {
  const [file, setFile] = useState<File | null>(null),
    [uploading, setUploading] = useState(false),
    request = useRef(""),
    lock = useRef(false);
  async function upload(value: File) {
    if (lock.current) return;
    lock.current = true;
    setUploading(true);
    onBusy(true);
    onError("");
    const body = new FormData();
    body.append("file", value);
    body.append("requestId", request.current);
    try {
      const { data } = await http.post<MessageFile>(
        `/v1/messaging/conversations/${conversation}/files`,
        body,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 20000 },
      );
      onFiles([...files, data]);
      setFile(null);
    } catch {
      onError(
        "Dépôt non confirmé : vérifiez le format, le quota et la disponibilité de l’antivirus, puis réessayez.",
      );
    } finally {
      lock.current = false;
      setUploading(false);
      onBusy(false);
    }
  }
  return (
    <>
      {/* Le champ de fichier reste dans le libellé, hors de vue : le libellé fait bouton. */}
      <label className="messaging-ghost">
        <Paperclip size={15} aria-hidden="true" />
        <span>
          Joindre un fichier{" "}
          <span className="messaging-sr-only">(PNG, JPEG, PDF · 5 Mio)</span>
        </span>
        <input
          type="file"
          className="messaging-sr-only"
          accept="image/png,image/jpeg,application/pdf"
          disabled={disabled || uploading || files.length >= 5}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) {
              setFile(f);
              request.current = crypto.randomUUID();
              void upload(f);
            }
          }}
        />
      </label>
      {uploading && (
        <p role="status" className="messaging-hint">
          Contrôle antivirus…
        </p>
      )}
      {file && !uploading && (
        <button
          type="button"
          className="messaging-ghost"
          disabled={disabled}
          onClick={() => void upload(file)}
        >
          Réessayer le dépôt
        </button>
      )}
      {files.map((f) => (
        <p key={f.id} className="messaging-chip">
          {f.name}{" "}
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => onFiles(files.filter((x) => x.id !== f.id))}
          >
            Retirer la pièce jointe
          </button>
        </p>
      ))}
    </>
  );
}
